import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => localStorage.getItem('gv_admin_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('gv_admin_token');
    localStorage.removeItem('gv_admin_user');
    setToken(null);
    setUser(null);
  }, []);

  // On mount: restore from localStorage immediately, then verify + refresh from server
  useEffect(() => {
    const verify = async () => {
      const stored = localStorage.getItem('gv_admin_token');

      if (!stored) {
        setLoading(false);
        return;
      }

      // Restore immediately from cache so UI renders without waiting for network
      const storedUser = localStorage.getItem('gv_admin_user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          setToken(stored);
        } catch { /* bad JSON */ }
      }

      // Hit /me to verify token and get fresh user data (including latest role + allowedEvents)
      try {
        const res = await axios.get(`${API_BASE}/auth/admin/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (res.data.success && res.data.user) {
          setUser(res.data.user);
          setToken(stored);
          localStorage.setItem('gv_admin_user', JSON.stringify(res.data.user));
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          // Token is expired or invalid — clear everything
          logout();
        }
        // Network error or 404 (/me not deployed yet) — keep cached session
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [logout]);

  const login = async ({ email, password }) => {
    const res = await axios.post(`${API_BASE}/auth/admin/login`, { email, password });
    if (!res.data.success) throw new Error(res.data.message || 'Login failed');
    const { token: t, user: u } = res.data;
    localStorage.setItem('gv_admin_token', t);
    localStorage.setItem('gv_admin_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
    // Return the user so the caller can read role.isSuperAdmin immediately
    // without waiting for a React state update cycle
    return u;
  };

  /**
   * hasPermission(key)
   * Returns true if the current admin has the given permission key.
   * Super admins always return true.
   * Usage: hasPermission('events:edit')
   */
  const hasPermission = useCallback((key) => {
    if (!user?.role) return false;
    if (user.role.isSuperAdmin) return true;
    return !!user.role.permissions?.[key];
  }, [user]);

  /**
   * canAccessEvent(eventId)
   * Returns true if the current admin is allowed to open this event.
   * - Super admins: always true
   * - Empty allowedEvents array: no restriction, all events accessible
   * - Non-empty allowedEvents array: only those specific event IDs are allowed
   */
  const canAccessEvent = useCallback((eventId) => {
    if (!user?.role) return false;
    if (user.role.isSuperAdmin) return true;
    const allowed = user.allowedEvents;
    if (!allowed || allowed.length === 0) return true;
    return allowed.some(e => {
      const id = typeof e === 'object' ? (e._id || e.id) : e;
      return id?.toString() === eventId?.toString();
    });
  }, [user]);

  /**
   * canAccessLobby(lobbyId)
   * Returns true if the current admin is allowed to access this lobby/booth.
   * - Super admins: always true
   * - Empty allowedLobbies: no restriction
   * - Non-empty allowedLobbies: only those IDs allowed
   */
  const canAccessLobby = useCallback((lobbyId) => {
    if (!user?.role) return false;
    if (user.role.isSuperAdmin) return true;
    const allowed = user.allowedLobbies;
    if (!allowed || allowed.length === 0) return true;
    return allowed.some(l => {
      const id = typeof l === 'object' ? (l._id || l.id) : l;
      return id?.toString() === lobbyId?.toString();
    });
  }, [user]);

  /**
   * canAccessForm(formId)
   * Returns true if the current admin is allowed to access this form.
   * - Super admins: always true
   * - Empty allowedForms: no restriction
   * - Non-empty allowedForms: only those IDs allowed
   */
  const canAccessForm = useCallback((formId) => {
    if (!user?.role) return false;
    if (user.role.isSuperAdmin) return true;
    const allowed = user.allowedForms;
    if (!allowed || allowed.length === 0) return true;
    return allowed.some(f => {
      const id = typeof f === 'object' ? (f._id || f.id) : f;
      return id?.toString() === formId?.toString();
    });
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      isAuthenticated: !!token && !!user,
      isSuperAdmin:    !!user?.role?.isSuperAdmin,
      isLobbyAdmin:    !!user?.isLobbyAdmin,
      lobbyAdminMode:  user?.lobbyAdminMode || 'chat_only',
      hasPermission,
      canAccessEvent,
      canAccessLobby,
      canAccessForm,
    }}>
      {children}
    </AuthContext.Provider>
  );
};